/**
 * Passkey step-up helpers for admin consoles (NEXUS-586 phase 3) — the browser half of
 * the gateway's /auth/mfa/* endpoints, via @simplewebauthn/browser.
 */
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { NexusAdminApiError } from './api.js';
export const getMfaStatus = (f) => f('/auth/mfa/status');
/** Face ID / Touch ID / security key → a new access token for this session with amr 'mfa'. */
export async function stepUpWithPasskey(f) {
    const { options } = await f('/auth/mfa/assert/options', { method: 'POST', json: {} });
    const response = await startAuthentication({ optionsJSON: options });
    return f('/auth/mfa/assert/verify', { method: 'POST', json: { response } });
}
export function stepUpWithRecoveryCode(f, code) {
    return f('/auth/mfa/recovery-code', { method: 'POST', json: { code } });
}
/** Register a passkey. `password` is needed for the FIRST one unless the session already stepped up. */
export async function addPasskey(f, name, password) {
    const { options } = await f('/auth/mfa/passkeys/registration/options', {
        method: 'POST', json: { name, ...(password ? { password } : {}) },
    });
    const response = await startRegistration({ optionsJSON: options });
    return f('/auth/mfa/passkeys/registration/verify', { method: 'POST', json: { response } });
}
/** True when this browser can do WebAuthn at all (the button is hidden otherwise). */
export function passkeySignInAvailable() {
    return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function';
}
/**
 * NEXUS-586: "Sign in with passkey" — no email or password; the passkey names the account and
 * counts as the admin step-up. `apiBase` is the gateway base the host uses for /auth/login.
 * Throws NexusAdminApiError (codes as for mfaErrorText) or the browser's NotAllowedError.
 */
export async function signInWithPasskey(apiBase, opts = {}) {
    const base = apiBase.replace(/\/+$/, '');
    const post = async (path, json) => {
        const res = await fetch(`${base}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(json) });
        const text = await res.text();
        let body = {};
        try {
            body = text ? JSON.parse(text) : {};
        }
        catch {
            body = {};
        }
        if (!res.ok) {
            throw new NexusAdminApiError({ status: res.status, code: String(body.code ?? `HTTP_${res.status}`), message: String(body.message ?? res.statusText) }, body);
        }
        return body;
    };
    const { challengeId, options } = await post('/auth/passkey-login/options', {});
    const response = await startAuthentication({ optionsJSON: options });
    return post('/auth/passkey-login/verify', { challengeId, response, ...(opts.sessionKind ? { sessionKind: opts.sessionKind } : {}) });
}
/** Plain-language text for a failed step-up / passkey call. */
export function mfaErrorText(e) {
    if (e instanceof NexusAdminApiError) {
        switch (e.code) {
            case 'MFA_FAILED': return 'That did not work. Try again.';
            case 'ACCOUNT_LOCKED': return 'Too many failed attempts — the account is locked for a while. Reset your password to unlock it now.';
            case 'INVALID_CREDENTIALS': return 'That password is not correct.';
            case 'CHALLENGE_EXPIRED': return 'That took too long. Try again.';
            case 'NO_PASSKEY': return 'No passkey is registered on this account yet.';
            case 'PASSKEY_NOT_RECOGNIZED': return 'That passkey is not set up for an account here. Sign in with your email and password, then add a passkey under Profile → Security.';
            case 'MFA_LOCKED': return 'Too many failed passkey attempts. Try again later, or sign in with your password.';
            case 'PASSKEY_EXISTS': return 'This passkey is already registered.';
            case 'PASSKEY_NOT_VERIFIED': return 'The passkey could not be verified. Try again.';
            case 'ADMIN_MFA_REQUIRED': return 'Confirm with your passkey (or a recovery code) first.';
            case 'SESSION_REQUIRED':
            case 'SESSION_ENDED': return 'Your session has ended. Sign in again.';
            case 'WEBAUTHN_NOT_CONFIGURED': return 'Passkeys are not set up on this Nexus instance yet.';
            default: return e.message;
        }
    }
    const name = e?.name;
    if (name === 'NotAllowedError' || name === 'AbortError')
        return 'Cancelled, or the passkey prompt timed out.';
    if (name === 'InvalidStateError')
        return 'This device already has a passkey for this account.';
    return e instanceof Error ? e.message : String(e);
}
/** Recovery codes as a downloadable .txt. */
export function recoveryCodesText(codes, account, site) {
    return [
        `Recovery codes — ${site}${account ? ` (${account})` : ''}`,
        `Created ${new Date().toISOString()}`,
        '',
        'Each code works once, in place of your passkey. Keep them somewhere safe and offline.',
        '',
        ...codes,
        '',
    ].join('\n');
}
//# sourceMappingURL=mfa.js.map